import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Tooltip,
} from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridToolbarContainer,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';
import { TRPCClientError } from '@trpc/client';
import AddIcon from '@mui/icons-material/Add';
// TODO update input when api is ready
import type {
  ShipMethods,
  ShipMethodsCreateInput,
  ShipMethodsUpdateInput,
} from '@repo/api-contract';

import { useDispatch, useSelector, pushToast } from '../../../state';
import { closeModal, setLoading } from '../../../state/shipMethodsModalSlice';
import DraggablePaper from '../DraggablePaper';
import { serverError } from '../constants/errors';
import { useTranslation } from 'react-i18next';
import { client } from '../../../util';
import { usePersistedDataGridColumnPreferences } from '../../../util/hooks';
import type { DataGridTableId } from '../../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'ship-methods' satisfies DataGridTableId;
const KEY_SHIPPING_METHODS = 'team.Modals.ShippingMethods';
const KEY_GLOBAL_COMMON = 'team.Global.Common';
const KEY_GLOBAL_BUTTONS = 'team.Global.Buttons';
const columnPadding = '0 2px';

export default function ShipMethodsModal() {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tMS } = useTranslation([], {
    keyPrefix: 'team.Modals.ShippingMethods',
  });

  const dispatch = useDispatch();
  const { openShipMethodsModal, loading } = useSelector(
    ({ shipMethodsModalSlice }) => shipMethodsModalSlice,
  );

  const [shipMethods, setShipMethods] = React.useState<ShipMethods[]>([]);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );
  const apiRef = useGridApiRef();

  const handleClose = () => {
    dispatch(closeModal());
  };

  // -------- Fetch list when modal opens --------
  React.useEffect(() => {
    if (!openShipMethodsModal) return;
    (async () => {
      dispatch(setLoading(true));
      try {
        const resp = await client.allShippingMethods.query();
        setShipMethods(resp.results);
      } catch (error) {
        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
      }
      dispatch(setLoading(false));
    })();
  }, [openShipMethodsModal, dispatch]);

  // -------- Toolbar: add row (+) --------
  function Toolbar() {
    const handleAddRow = () => {
      const tempId = `temp-${uuidv4()}`;
      const newRow = {
        id: tempId,
        ShipMethodId: tempId, // local temp id to satisfy grid id
        method: '',
        default: shipMethods.length === 0, // first created becomes default
        isNew: true,
      };
      setShipMethods((prev) => [...prev, newRow as any]);
      setRowModesModel((m) => ({
        ...m,
        [tempId]: { mode: GridRowModes.Edit },
      }));
    };

    return (
      <GridToolbarContainer>
        <Tooltip title={tGlobal('ADD ROW')}>
          <IconButton
            color="primary"
            onClick={handleAddRow}
            sx={{ ml: 'auto' }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </GridToolbarContainer>
    );
  }

  // -------- Default toggle (radio) --------
  const handleMakeDefault = async (rowId: GridRowId) => {
    const current = shipMethods.find((s) => s.ShipMethodId === rowId);
    if (!current || current.default) return;
    // skip temp rows
    if (String(current.ShipMethodId).startsWith('temp-')) {
      dispatch(
        pushToast({
          message: 'Please save this shipping method before making it default.',
          type: 'info',
        }),
      );
      return;
    }
    dispatch(setLoading(true));
    try {
      await client.setDefaultShippingMethod.mutate(current.ShipMethodId);
      setShipMethods((prev) =>
        prev.map((s) => ({
          ...s,
          default: s.ShipMethodId === current.ShipMethodId,
        })),
      );
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    }
    dispatch(setLoading(false));
  };

  // -------- Row edit lifecycle --------
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      // keep edit active until user explicitly saves/cancels
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };
  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };
  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
    // If it was a brand new local row, remove it on cancel
    setShipMethods((prev) =>
      prev.filter(
        (r) => r.ShipMethodId !== id || !String(id).startsWith('temp-'),
      ),
    );
  };

  const handleDeleteClick = (id: GridRowId) => async () => {
    dispatch(setLoading(true));
    try {
      // If it's a temp row, just drop it
      if (String(id).startsWith('temp-')) {
        setShipMethods((prev) => prev.filter((r) => r.ShipMethodId !== id));
      } else {
        await client.deleteShippingMethod.mutate(id as string);
        setShipMethods((prev) => prev.filter((r) => r.ShipMethodId !== id));
      }
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    }
    dispatch(setLoading(false));
  };

  // Create/Update on save
  const processRowUpdate = async (newRow: GridRowModel) => {
    const isTemp = String(newRow.id).startsWith('temp-');
    dispatch(setLoading(true));
    try {
      if (isTemp) {
        // CREATE
        const payload: ShipMethodsCreateInput = {
          method: newRow.method ?? '',
          default: shipMethods.length === 0 ? true : Boolean(newRow.default),
        };
        const created = await client.createShippingMethod.mutate(payload);
        setShipMethods((prev) =>
          prev.map((r) =>
            r.ShipMethodId === newRow.id ? (created as any) : r,
          ),
        );
        return { ...created, id: created.ShipMethodId };
      } else {
        // UPDATE
        const payload: ShipMethodsUpdateInput = {
          ShipMethodId: newRow.id as string,
          method: newRow.method ?? '',
          default: newRow.default ?? false,
        };
        const updated = await client.updateShippingMethod.mutate(payload);
        setShipMethods((prev) =>
          prev.map((r) =>
            r.ShipMethodId === updated.ShipMethodId ? (updated as any) : r,
          ),
        );
        return { ...updated, id: updated.ShipMethodId };
      }
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      throw error; // let grid revert
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRowModesModelChange = (m: GridRowModesModel) =>
    setRowModesModel(m);

  // -------- Columns --------
  const baseColumns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'method',
        headerName: 'Method',
        flex: 1,
        editable: true,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Method"
            keyPrefix={KEY_SHIPPING_METHODS}
            params={params}
          />
        ),
      },
      {
        field: 'default',
        headerName: 'Default',
        flex: 0.6,
        sortable: false,
        filterable: false,
        editable: false,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Default"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params) => {
          const checked = Boolean(params.row.default);
          return (
            <Radio
              checked={checked}
              onClick={() => handleMakeDefault(params.id)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 90,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Actions"
            keyPrefix={KEY_GLOBAL_BUTTONS}
            params={params}
          />
        ),
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          return isInEditMode
            ? [
                <GridActionsCellItem
                  key="save"
                  icon={<SaveIcon />}
                  label="Save"
                  sx={{ color: 'primary.main' }}
                  onClick={handleSaveClick(id)}
                />,
                <GridActionsCellItem
                  key="cancel"
                  icon={<CancelIcon />}
                  label="Cancel"
                  onClick={handleCancelClick(id)}
                  color="inherit"
                />,
              ]
            : [
                <GridActionsCellItem
                  key="edit"
                  icon={<EditIcon />}
                  label="Edit"
                  onClick={handleEditClick(id)}
                  color="inherit"
                />,
                <GridActionsCellItem
                  key="delete"
                  icon={<DeleteIcon />}
                  label="Delete"
                  onClick={handleDeleteClick(id)}
                  color="inherit"
                />,
              ];
        },
      },
    ],
    [
      rowModesModel,
      handleMakeDefault,
      handleSaveClick,
      handleCancelClick,
      handleEditClick,
      handleDeleteClick,
    ],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
  });

  const columns = React.useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <Dialog
      open={openShipMethodsModal}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {tMS('Shipping Methods')}
      </DialogTitle>
      <IconButton
        disabled={loading}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box
            height="400px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <Box sx={{ height: 420, width: '100%' }}>
            <DataGridPro
              apiRef={apiRef}
              columnHeaderHeight={56}
              sx={{
                border: 0,
                '& .MuiDataGrid-cell': { padding: columnPadding },
                '& .MuiDataGrid-columnHeader': { padding: columnPadding },
                '.MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                },
              }}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              disableMultipleRowSelection
              hideFooter
              onRowEditStop={handleRowEditStop}
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              processRowUpdate={processRowUpdate}
              slots={{ toolbar: Toolbar }}
              rows={shipMethods.map((m) => ({
                ...m,
                id: m.ShipMethodId,
              }))}
              columns={columns}
              columnVisibilityModel={persistedPrefs.columnVisibilityModel}
              onColumnVisibilityModelChange={
                persistedPrefs.onColumnVisibilityModelChange
              }
              onColumnWidthChange={persistedPrefs.onColumnWidthChange}
              onColumnOrderChange={persistedPrefs.onColumnOrderChange}
              pageSizeOptions={[shipMethods.length || 0]}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading}
            variant="outlined"
            color="primary"
            onClick={handleClose}
          >
            {tGlobal('Close')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
